(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealSelector = '#MainContent > .shopify-section:not(:first-child)';
  let revealObserver;
  let scrollTicking = false;

  const progress = document.createElement('div');
  progress.className = 'zuofun-scroll-progress';
  progress.setAttribute('aria-hidden', 'true');
  document.body.append(progress);

  const updateScrollState = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollRange = document.documentElement.scrollHeight - window.innerHeight;
    const scrollProgress = scrollRange > 0 ? Math.min(scrollTop / scrollRange, 1) : 0;

    document.body.classList.toggle('zuofun-has-scrolled', scrollTop > 12);
    progress.style.setProperty('--zuofun-scroll-progress', scrollProgress.toFixed(4));
    scrollTicking = false;
  };

  const onScroll = () => {
    if (scrollTicking) return;
    scrollTicking = true;
    window.requestAnimationFrame(updateScrollState);
  };

  const revealImmediately = (element) => element.classList.add('is-zuofun-visible');

  const prepareReveals = (scope = document) => {
    const sections = scope.matches?.(revealSelector)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(revealSelector) || []);

    sections.forEach((section, index) => {
      // A transformed ancestor changes the containing block for fixed UI.
      // Keep product sticky purchase bars anchored to the viewport bottom.
      if (section.querySelector('sticky-add-to-cart')) {
        section.removeAttribute('data-zuofun-reveal');
        section.classList.remove('is-zuofun-visible');
        section.style.removeProperty('--zuofun-reveal-delay');
        return;
      }

      if (section.hasAttribute('data-zuofun-reveal')) return;
      section.setAttribute('data-zuofun-reveal', '');
      section.style.setProperty('--zuofun-reveal-delay', `${Math.min(index * 35, 140)}ms`);

      if (reduceMotion.matches || !revealObserver) revealImmediately(section);
      else revealObserver.observe(section);
    });
  };

  const prepareLazyVideos = (scope = document) => {
    const deferredVideos = Array.from(scope.querySelectorAll?.('deferred-media[autoplay]') || []);

    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;

    const videoObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const posterButton = entry.target.querySelector('.deferred-media__poster-button');
          if (posterButton) posterButton.click();
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '320px 0px', threshold: 0.01 }
    );

    deferredVideos.forEach((video) => {
      if (!video.querySelector('video, iframe')) videoObserver.observe(video);
    });
  };

  const activateVisibleHeroVideo = () => {
    document.querySelectorAll('[data-testid*="hero-"][data-testid*="-video-"]').forEach((video) => {
      const isVisible = video.getClientRects().length > 0 && video.getBoundingClientRect().width > 0;
      if (isVisible) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  };

  if (!reduceMotion.matches && 'IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          revealImmediately(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
  }

  document.documentElement.classList.add('zuofun-motion-ready');
  prepareReveals();
  prepareLazyVideos();
  activateVisibleHeroVideo();
  updateScrollState();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', activateVisibleHeroVideo, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    prepareReveals(event.target);
    prepareLazyVideos(event.target);
    activateVisibleHeroVideo();
  });
  reduceMotion.addEventListener('change', () => {
    document.querySelectorAll('[data-zuofun-reveal]').forEach(revealImmediately);
  });
})();
